import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { endpoints, postFunc } from '../../../api'
import { showToast } from '../../../utils/toast'
import useQuery from '../../../hooks/useQuery'
import { Link, useNavigate } from 'react-router-dom'
import Spinner from '../../../components/Spinner'
import FormInput from '../../../components/Input'
import DefaultButton from '../../../components/Button/Button'
import DarkSafeltoLogo from '../../../assets/icons/SafeltoLogo'
import SafeltoShortLogo from '../../../assets/icons/SalfetoShortLogo.svg'

interface ValidateResetTokenResponse {
  success: boolean
}

const ResetPasswordPage = () => {
  const [tokenVerified, setTokenVerified] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const navigate = useNavigate()

  const query = useQuery()
  const resetToken = query.get('token')

  useEffect(() => {
    if (!resetToken) {
      showToast({
        title: 'Invalid reset link',
        description:
          'Your reset link is invalid or expired. Please check the link and try again',
        status: 'error',
        id: 'reset-link',
      })
      navigate('/auth/forgot-password')
      return
    }

    postFunc(`${endpoints.validateToken}?token=${resetToken}`)
      .then((data) => {
        const responseData = data as ValidateResetTokenResponse
        setTokenVerified(responseData.success)
      })
      .catch(() => {
        setTokenVerified(false)
      })
      .finally(() => {
        setVerifying(false)
      })
  }, [navigate, resetToken])

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    async onSubmit(values) {
      if (loading || !formik.isValid || !tokenVerified) {
        return
      }
      setLoading(true)
      try {
        await postFunc(`${endpoints.resetPassword}?token=${resetToken}`, {
          password: values.password,
        })
        showToast({
          title: 'Password reset',
          description: 'Your password has been changed successfully',
          status: 'success',
          id: 'password-reset',
        })
        sessionStorage.setItem('passwordResetComplete', 'true')
        navigate('/auth/login')
      } catch {
        setTokenVerified(false)
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <>
      <div className="p-10">
        <div className="">
          <Link to={'/'}>
            <DarkSafeltoLogo />
          </Link>
        </div>

        {verifying ? (
          <div className="mt-10 flex justify-center items-center">
            <span className="mr-2">Verifying link</span>
            <Spinner color="green-500" loading={verifying} />
          </div>
        ) : (
          <div className="py-10 flex flex-col justify-center items-center gap-14">
            <div className="flex flex-col items-center justify-center gap-10">
              <div>
                {' '}
                <img src={SafeltoShortLogo} alt="Logo" className="w-10 h-10" />
              </div>
              <div className="">
                <p className="font-bold text-lg text-center">Reset password</p>
                <p className="font-medium text-base text-gray-400">
                  Please enter new password
                </p>
              </div>
            </div>
            <form onSubmit={formik.handleSubmit} className="w-full max-w-md">
              <div className="flex flex-col items-center justify-center gap-10">
                <div className="w-full">
                  <FormInput
                    placeholder="Enter password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    name="password"
                    type="password"
                    size="lg"
                    useToggle={true}
                  />
                </div>
                <div className="w-full mb-10">
                  <FormInput
                    placeholder="Re-enter password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    name="confirmPassword"
                    type="password"
                    useToggle={true}
                  />
                  {formik.errors.confirmPassword && (
                    <p className="text-sm text-red-500">
                      {formik.errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
              {!tokenVerified && (
                <p className="text-sm text-red-500 text-center">
                  Reset is invalid or expired. Request a new link{' '}
                  <a href="/auth/forgot-password" className="text-primary-300">
                    here
                  </a>
                </p>
              )}
              <DefaultButton
                background="gray.900"
                color="white"
                type={!tokenVerified ? 'button' : 'submit'}
                size="lg"
                disabled={loading || !tokenVerified}
                isLoading={loading}
                loadingText="Submitting..."
                className="w-full p-7 mt-2"
                style={{
                  backgroundColor: !formik.values.password ? '#DEE2E6' : 'black',
                }}
              >
                Continue
              </DefaultButton>
            </form>
          </div>
        )}
      </div>
    </>
  )
}

export default ResetPasswordPage
